// plan-change-monitoring.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'default',
  tag: 'Plan Intelligence',

  html: `
<p>Medicare plans change every year. Benefits shift. Premiums adjust. Networks evolve. Formularies update. If you're not monitoring these changes and how they affect your specific clients, you're flying blind—and your clients will be surprised when something they counted on isn't there anymore.</p>

<p>Plan change monitoring is about staying ahead of changes so you can inform clients before they find out the hard way, and intervene before changes become reasons to switch.</p>

<h2>What Changes to Monitor</h2>

<h3>Benefit Changes</h3>
<ul>
<li><strong>Premium:</strong> Increases, decreases, or restructuring</li>
<li><strong>Deductibles:</strong> Changes to annual deductible amounts</li>
<li><strong>Copays/coinsurance:</strong> Cost-sharing adjustments</li>
<li><strong>Out-of-pocket maximum:</strong> MOOP changes</li>
<li><strong>Extra benefits:</strong> OTC allowances, dental, vision, fitness, meals</li>
<li><strong>Part D coverage:</strong> Drug deductibles, coverage gap, catastrophic</li>
</ul>

<h3>Network Changes</h3>
<ul>
<li><strong>Provider terminations:</strong> Doctors leaving the network</li>
<li><strong>Hospital changes:</strong> Facilities in/out of network</li>
<li><strong>Pharmacy network:</strong> Preferred pharmacies changing</li>
<li><strong>Geographic coverage:</strong> Service area modifications</li>
<li><strong>Tier restructuring:</strong> Providers moving between cost tiers</li>
</ul>

<h3>Formulary Changes</h3>
<ul>
<li><strong>Drug removals:</strong> Medications dropped from formulary</li>
<li><strong>Tier changes:</strong> Drugs moving to higher/lower cost tiers</li>
<li><strong>Prior authorization:</strong> New PA requirements added</li>
<li><strong>Step therapy:</strong> New requirements to try other drugs first</li>
<li><strong>Quantity limits:</strong> Restrictions on fill amounts</li>
</ul>

<h3>Quality Indicators</h3>
<ul>
<li><strong>Star ratings:</strong> Overall and component ratings</li>
<li><strong>Customer satisfaction:</strong> CAHPS survey trends</li>
<li><strong>Complaint rates:</strong> CMS complaint data</li>
<li><strong>Appeals/denials:</strong> Prior auth approval patterns</li>
</ul>

<h2>When Changes Are Announced</h2>

<h3>Annual Notice of Change (ANOC)</h3>
<p>Carriers must send ANOCs by September 30 for the following plan year. These detail all significant changes taking effect January 1.</p>

<p><strong>What to do:</strong> Review ANOCs for every plan in your book as soon as they're available. Identify which clients are affected by which changes.</p>

<h3>Evidence of Coverage (EOC)</h3>
<p>The full plan document is sent by October 15. Contains complete benefit details for the upcoming year.</p>

<p><strong>What to do:</strong> Cross-reference EOC with ANOC to understand full impact of changes.</p>

<h3>Mid-Year Changes</h3>
<p>Some changes can happen during the plan year:</p>
<ul>
<li>Network changes: Usually 30-60 day notice</li>
<li>Formulary changes: Varies by type (30-60 day typically)</li>
<li>Service area changes: Advance notice required</li>
</ul>

<p><strong>What to do:</strong> Monitor carrier communications year-round, not just September.</p>

<h2>Building a Monitoring System</h2>

<h3>Client-Level Tracking</h3>
<p>For each client, know:</p>
<ul>
<li>Current plan</li>
<li>Key providers they use</li>
<li>Important medications</li>
<li>Benefits they value</li>
</ul>

<p>This baseline lets you assess impact when plan changes are announced.</p>

<h3>Plan-Level Tracking</h3>
<p>For each plan in your book, track:</p>
<ul>
<li>Announced changes for next year</li>
<li>Current year mid-year changes</li>
<li>Historical change patterns (volatile or stable?)</li>
<li>Star rating trends</li>
</ul>

<h3>Alert Creation</h3>
<p>When a change is identified, create alerts for affected clients:</p>
<ul>
<li>Severity (significant vs. minor)</li>
<li>Action needed (proactive outreach vs. mention at review)</li>
<li>Timing (how soon does this take effect?)</li>
</ul>

<h2>Impact Assessment Framework</h2>

<p>Not all changes require the same response. Assess impact:</p>

<h3>High Impact (Immediate Outreach)</h3>
<ul>
<li>Client's primary doctor leaving network</li>
<li>Key medication dropped or restricted</li>
<li>Significant premium increase (20%+)</li>
<li>Benefit they actively use being removed</li>
<li>Plan dropping below 3 stars</li>
</ul>

<h3>Medium Impact (Discuss at Review)</h3>
<ul>
<li>Moderate cost-sharing increases</li>
<li>Secondary provider network changes</li>
<li>Drug tier changes (not removal)</li>
<li>Benefit reductions they may not use</li>
</ul>

<h3>Low Impact (Note for Reference)</h3>
<ul>
<li>Minor cost adjustments</li>
<li>Changes to benefits they don't use</li>
<li>Structural changes that don't affect them</li>
</ul>

<h2>Communicating Changes to Clients</h2>

<h3>For High-Impact Changes</h3>
<p>Reach out immediately:</p>
<p><em>"Hi [Name], I wanted to let you know about a change to your plan that affects you. [Specific change]. Let's talk through your options..."</em></p>

<p>Be prepared to discuss:</p>
<ul>
<li>What exactly is changing</li>
<li>How it affects them specifically</li>
<li>What options they have</li>
<li>Your recommendation</li>
</ul>

<h3>For Medium-Impact Changes</h3>
<p>Include in annual review conversation:</p>
<p><em>"There are a few changes to your plan next year I want to make sure you know about..."</em></p>

<h3>For Low-Impact Changes</h3>
<p>Mention if relevant, but don't over-communicate minor changes that don't affect them.</p>

<h2>Pre-AEP Monitoring Workflow</h2>

<p>September routine:</p>
<ol>
<li><strong>Week 1:</strong> Gather ANOCs for all plans in your book</li>
<li><strong>Week 2:</strong> Review changes, identify affected clients</li>
<li><strong>Week 3:</strong> Create impact alerts, prioritize outreach</li>
<li><strong>Week 4:</strong> Begin proactive communication to high-impact clients</li>
</ol>

<p>By October 15, you should know which clients face significant changes and have already started conversations with the highest-impact ones.</p>

<h2>Using Change Data for Enrollment Decisions</h2>

<p>Plan change history informs enrollment recommendations:</p>
<ul>
<li>Plans with volatile year-over-year changes may not serve clients well long-term</li>
<li>Plans with stable benefits/networks are lower maintenance</li>
<li>Declining Star ratings predict future problems</li>
<li>Frequent formulary churn indicates potential ongoing issues</li>
</ul>

<p>Factor stability into your plan recommendations, not just current-year benefits.</p>
  `,

  faqs: [
    {
      question: "When are Medicare plan changes announced?",
      answer: "Annual Notice of Change (ANOC) must be sent by September 30 for the following plan year. Evidence of Coverage (EOC) arrives by October 15. Mid-year changes (network, formulary) can happen with 30-60 days notice throughout the year."
    },
    {
      question: "What plan changes should I monitor most closely?",
      answer: "Focus on changes that directly affect your clients: key providers leaving networks, medications dropped or restricted, significant premium increases, and benefits they actively use being modified. Also track Star rating changes and overall plan stability patterns."
    },
    {
      question: "How do I assess which changes are high-impact vs. low-impact?",
      answer: "High impact: primary doctor leaving network, key medication affected, 20%+ premium increase, benefit they use removed, Star rating dropping below 3. Medium: moderate cost increases, secondary provider changes, drug tier changes. Low: minor adjustments to things they don't use."
    },
    {
      question: "How should I communicate high-impact changes to clients?",
      answer: "Reach out immediately—don't wait for annual review. Explain what's changing, how it affects them specifically, what options they have, and your recommendation. Be proactive; clients who hear from you first stay. Clients who find out themselves may leave."
    },
    {
      question: "Should plan stability factor into enrollment recommendations?",
      answer: "Yes. Plans with volatile year-over-year changes create more client friction and retention work. Stable plans with consistent networks and benefits are lower maintenance long-term. Factor historical stability into your recommendations, not just current benefits."
    }
  ],

  sources: [
    {
      title: "Medicare Plan Annual Notice of Change Requirements",
      url: "https://www.cms.gov/Medicare/Health-Plans/ManagedCareMarketing",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP monitors plan changes across your book and automatically creates alerts when clients are affected. Know about changes when they're announced—not when clients discover them at the doctor's office.`
};

export default content;
